import React from 'react';
import { View } from 'react-native';
import { Divider } from 'react-native-elements';
import Checkbox from '@/components/ui/therapist/Checkbox';
import { Input } from '@/components/ui/therapist/Input';
import { NeolexonIcon } from '@/components/NeolexonIcon';
import { DifficultyQuery, TemplateQuery } from '@/components/Template/types';
import { Text } from '@/components/Text';
import { FilterDrawer } from '@/components/ui/therapist/FilterDrawer';

import { styles } from './styles';
import colors from '@/config/colors';

interface TemplateFilterProps {
  isOpen: boolean;
  onClose: () => void;
  difficultyFilter?: DifficultyQuery | undefined;
  setDifficultyFilter: (value: DifficultyQuery | undefined) => void;
  typeFilter?: TemplateQuery | undefined;
  setTypeFilter?: (value: TemplateQuery | undefined) => void;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  resetFilter: () => void;
}

export const TemplateFilter: React.FC<TemplateFilterProps> = ({
  isOpen,
  onClose,
  difficultyFilter,
  setDifficultyFilter,
  typeFilter,
  setTypeFilter,
  searchTerm,
  setSearchTerm,
  resetFilter,
}) => {
  const setDifficultyFilterHelper = (
    value: 'easy,' | 'medium,' | 'hard,' | undefined,
  ) => {
    if (value && difficultyFilter?.includes(value)) {
      const newFilter = difficultyFilter.replace(value, '') as DifficultyQuery;
      setDifficultyFilter(newFilter);
      return;
    }
    if (value && ['easy,', 'medium,', 'hard,'].includes(value)) {
      const newFilter = difficultyFilter ? difficultyFilter + value : value;
      setDifficultyFilter(newFilter as DifficultyQuery);
      return;
    }
  };

  const setTypeFilterHelper = (value: 'Word,' | 'Sentence,' | undefined) => {
    if (value && typeFilter?.includes(value)) {
      const newFilter = typeFilter.replace(value, '') as TemplateQuery;
      setTypeFilter?.(newFilter);
      return;
    }
    if (value && ['Word,', 'Sentence,'].includes(value)) {
      const newFilter = typeFilter ? typeFilter + value : value;
      setTypeFilter?.(newFilter as TemplateQuery);
      return;
    }
  };

  return (
    <FilterDrawer
      isOpen={isOpen}
      onClose={onClose}
      onApply={onClose}
      onReset={resetFilter}
    >
      <View style={{ paddingHorizontal: 20 }}>
        <Input
          label="Titel oder Beschreibung"
          value={searchTerm}
          onChange={setSearchTerm}
          iconLeft="magnifyingGlass"
        />
        <View style={{ paddingVertical: 10 }}>
          <Divider width={1} color={colors.newUI.therapist.primaryBlue10} />
        </View>
      </View>
      {setTypeFilter ? (
        <View style={{ paddingHorizontal: 20 }}>
          <View>
            <Text style={{ fontSize: 18, marginBottom: 10, fontWeight: '600' }}>
              Kategorie
            </Text>
          </View>
          <View style={{ gap: 10, marginBottom: 20 }}>
            <View style={styles.checkboxWrapper}>
              <Checkbox
                isSelected={typeFilter?.includes('Word,') || false}
                onPress={() => setTypeFilterHelper('Word,')}
                label="Wörterset"
              />
              <View style={{ paddingRight: 20 }}>
                <NeolexonIcon name="letter" isInverted={false} size={20} />
              </View>
            </View>
            <View style={styles.checkboxWrapper}>
              <Checkbox
                isSelected={typeFilter?.includes('Sentence,') || false}
                onPress={() => setTypeFilterHelper('Sentence,')}
                label="Sätzeset"
              />
              <View style={{ paddingRight: 20 }}>
                <NeolexonIcon name="sentence" isInverted={false} size={20} />
              </View>
            </View>
          </View>
          <View style={{ paddingVertical: 10 }}>
            <Divider width={1} color={colors.newUI.therapist.primaryBlue10} />
          </View>
        </View>
      ) : null}
      <View style={{ paddingHorizontal: 20 }}>
        <View>
          <Text style={{ fontSize: 18, fontWeight: '600', marginBottom: 10 }}>
            Schwierigkeit
          </Text>
        </View>
        <View style={{ gap: 10, marginBottom: 20 }}>
          <View style={styles.checkboxWrapper}>
            <Checkbox
              isSelected={difficultyFilter?.includes('easy,') || false}
              onPress={() => setDifficultyFilterHelper('easy,')}
              label="Leicht"
            />
            <View style={{ paddingRight: 20 }}>
              <NeolexonIcon name="weightEasy" isInverted={false} size={20} />
            </View>
          </View>
          <View style={styles.checkboxWrapper}>
            <Checkbox
              isSelected={difficultyFilter?.includes('medium,') || false}
              onPress={() => setDifficultyFilterHelper('medium,')}
              label="Mittel"
            />
            <View style={{ paddingRight: 20 }}>
              <NeolexonIcon name="weightMedium" isInverted={false} size={20} />
            </View>
          </View>
          <View style={styles.checkboxWrapper}>
            <Checkbox
              isSelected={difficultyFilter?.includes('hard,') || false}
              onPress={() => setDifficultyFilterHelper('hard,')}
              label="Schwer"
            />
            <View style={{ paddingRight: 20 }}>
              <NeolexonIcon name="weightHard" isInverted={false} size={20} />
            </View>
          </View>
          <View style={{ paddingVertical: 10 }}>
            <Divider width={1} color={colors.newUI.therapist.primaryBlue10} />
          </View>
        </View>
      </View>
    </FilterDrawer>
  );
};
